<template>
  <div class="content-wrapper campaign-container">
    <div class="row mb-4 pb-3">
      <div class="col-md-9 col-sm-12">
        <loading-skeleton
          v-if="preloaderCampaign"
          class="campaigns-select-skeleton"
          type="card"
          :card="{ rows: 5 }"
        />
        <div v-else class="campaign-container__campaign">
          <div class="campaign-container__campaign__actions">
            <switch-button
              :checked="campaign.enabled"
              :hover-title="{ enabled: 'Disable', disabled: 'Enable' }"
              :update="(value) => $_updateStatus(campaign, value)"
            />
          </div>
          <div class="campaign-container__campaign__name">
            <div class="d-flex align-items-center campaign-name">
              <template v-if="account.type">
                <img class="logo-icon" v-b-tooltip.hover.top :title="account.name" :src="'/sh_static/accounts/'+account.type.uniqueName+'.png'">
              </template>
              <template v-if="tracker.type">
                <i class="fa fa-arrows-h divider" /> <img class="logo-icon" v-b-tooltip.hover.top :title="tracker.name" :src="'/sh_static/accounts/'+tracker.type.uniqueName+'.png'">
              </template>
              <span :title="campaign.name">{{ campaign.name }}</span>
              <div class="flags text-right">
                <div class="flag-item label-red" v-if="!campaign.is_whitelist" v-b-tooltip.hover.bottomleft title="Blacklist">B</div>
                <div class="flag-item label-light-blue" v-else-if="campaign.is_whitelist" v-b-tooltip.hover.bottomleft title="Whitelist">W</div>
                <div class="flag-item label-gray" v-if="campaign.unmanaged" v-b-tooltip.hover.bottomleft title="Campaign is not managed by Optimizer">U</div>
                <div class="flag-item label-blue" v-if="campaign.has_rules" v-b-tooltip.hover.bottomleft title="Has active rules">R</div>
                <div class="flag-item label-orange" v-if="campaign.manual_connected" v-b-tooltip.hover.bottomleft title="Manual connected">M</div>
              </div>
            </div>
          </div>

          <div class="campaign-container__campaign__status">
            <span
              v-b-tooltip.hover.topcenter
              :class="`status-item status-item-${campaign.status}`"
              :title="campaign.status"
              v-html="ui.campaign.status(campaign.status)"
            />
          </div>

          <template v-if="$route.params.type">
            <div v-for="(item, index) in editableFields[$route.params.type]" :key="index">
              <div class="campaign-container__campaign__budget" >
                <span class="label-tag">{{ item.name }}</span>
                <template v-if="item.key === 'cpc'">
                  <template v-if="campaign.traffic_source_unique_name == 'Kadam' && (campaign.byCountry[0] || {}).country">
                    <span style="margin-right:5px"> {{ (campaign.byCountry[0] || {}).country }} </span>
                  </template>
                  <live-edit
                    :key="`bidLiveEdit-${item.key}`"
                    v-model="campaign.cpc"
                    :prefix="symbols.get(campaign.traffic_source_currency) || '$'"
                    :format="ui.numFormat"
                    field-name="Bid"
                    :before-blur="value => campaign.traffic_source_unique_name === 'Kadam' ? $_changeCountryBid(campaign, value, campaign.byCountry[0].country) : $_updateBid(campaign, value, campaign.additional_fields.bid_type)"
                  />
                </template>
                <template v-else-if="item.key === 'budget'">
                  <span v-if="campaign.budget === 'null'">-</span>
                  <span v-else-if="campaign.budget === -1">Unlimited</span>
                  <live-edit
                    :key="`budgetLiveEdit-${item.key}`"
                    v-model="campaign.budget"
                    :prefix="symbols.get(campaign.traffic_source_currency) || '$'"
                    :format="ui.numFormat"
                    field-name="Budget"
                    :before-blur="value => $_updateBudget(campaign, value, campaign.budget_type)"
                  />
                </template>
                <template v-else-if="item.key === 'daily_budget'">
                  <span v-if="campaign.budget === 'null'">-</span>
                  <span v-else-if="campaign.budget === -1">Unlimited</span>
                  <live-edit
                    :key="`dailyBudgetLiveEdit-${item.key}`"
                    v-model="campaign.daily_budget"
                    :prefix="symbols.get(campaign.traffic_source_currency) || '$'"
                    :format="ui.numFormat"
                    field-name="Daily Budget"
                    :before-blur="value => $_updateDailyBudget(campaign, value, campaign.daily_budget_type)"
                  />
                </template>
              </div>
            </div>
          </template>

        </div>
      </div>

      <!-- <div class="col-md-3">
        <h2 class="content-title">
          Campaign
        </h2>
      </div> -->
      <div class="col-md-3 col-sm-12 ml-md-auto">
        <date-range
          v-model="dateRangePicker"
          @change="$_changeDateRangePicker"
        />
      </div>
    </div>

    <!--STATISTICS-->
    <div class="partial-reload mb-4 pb-1">
      <CampaignsStats
        :cost="campaign.cost"
        :revenue="campaign.tr_revenue"
        :net="campaign.tr_net"
        :roi="campaign.tr_roi"
        :cpc="campaign.avg_cpc"
        :cpa="campaign.tr_cpa"
        :epc="campaign.tr_epc"
        :est_cost="campaign.estimate_cost"
        :est_net="campaign.estimate_net"
        :est_roi="campaign.estimate_roi"
        :loading="preloaderCampaign"
      />
    </div>

    <!--Tab Header-->
    <div class="row campaign-container__tabs">
      <h5 style="margin: 0px;" class="col-5 col-sm-3 col-md-5 mr-auto">
        {{ activeTab.name }} <small v-if="activeTab.timezone" class="reporting-timezone-title">Reporting timezone: {{ $c_reportingTimezone }}</small>
      </h5>
      <div class="col-auto ml-auto campaign-container__tabs__content">
        <div v-for="tab in availableTabs" :key="tab.id" class="tab-item" @click="$_tabAction(tab)" :class="{ 'tab-item-active': activeTab.id === tab.id }">
          {{ tab.name }}
        </div>
      </div>
    </div>

    <loading-skeleton
      v-if="preloaderCampaign"
      type="table"
      :table="{ headItems: 6, bodyItems: 20, filters: true, search: true, filterItems: 2 }"
    />

    <!--Tab Component-->
    <component :is="activeTab.component" :campaign-loading="preloaderCampaign" :campaign="campaign" :account="account" :tracker="tracker" :date-range="dateRangePicker" :settings="$c_settings" />

  </div>
</template>

<script>
import CampaignsStats from '@sh/components/Campaigns/CampaignsStats.vue';
import OptimizerIcon from '@sh/components/Utils/OptimizerIcon.ts.vue';
import LiveEdit from '@sh/components/Utils/LiveEdit';
import LoadingSkeleton from '@sh/components/Utils/Skeleton/LoadingSkeleton.vue';
import moment from 'moment';
import notifications from '@sh/mixins/notifications';
import tableMixins from '@sh/mixins/table';
import campaignMixins from '@/mixins/campaign';
import _object from 'lodash/fp/object';
import _lang from 'lodash/fp/lang';
import config from './config';
import ui from '@sh/helpers/ui';
import symbols from '@sh/helpers/symbols';
import Domains from './Tabs/Domains';
import Rules from '@sh/views/Campaign/Tabs/Rules';
import Sources from './Tabs/Sources';
import Creatives from './Tabs/Creatives';
import Publishers from './Tabs/Publishers';
import Keywords from './Tabs/Keywords';
import SubIds from './Tabs/SubIds';
import Channels from './Tabs/Channels';
import Performance from './Tabs/Performance';

const FORMAT = 'YYYY-MM-DD';

export default {
  name: 'Campaign',
  components: {
    CampaignsStats,
    OptimizerIcon,
    LiveEdit,
    LoadingSkeleton,
    Domains,
    Rules,
    Sources,
    Creatives,
    Publishers,
    Keywords,
    SubIds,
    Channels,
    Performance,
  },
  mixins: [notifications, tableMixins, campaignMixins],
  data() {
    return {
      ui,
      symbols,
      campaign: {},
      account: {},
      tracker: {},
      dateRangePicker: {
        startDate: moment().subtract(29, 'days').startOf('day').format(FORMAT),
        endDate: moment().endOf('day').format(FORMAT),
      },
      settings: {},
      preloader: true,
      preloaderCampaign: true,
      availableTabs: [],
      activeTab: {},
      editableFields: {},
    };
  },
  computed: {
    $c_reportingTimezone() {
      let { timezone } = this.account.type;
      try {
        if (this.account.settings.timezone) timezone = this.account.settings.timezone;
      } catch (error) {
        // Do nothing
      }
      return timezone;
    },
    $c_settings() {
      const campSettings = this.campaign.settings ? _lang.cloneDeep(this.campaign.settings) : {};
      const globalSettings = _lang.cloneDeep(this.settings);
      return _object.merge(globalSettings, campSettings);
    },
  },
  async created() {
    this.preloader = true;
    /* *********** Set Date Range ************ */
    if (this.$route.query.startDate && this.$route.query.endDate) {
      this.dateRangePicker.startDate = this.$route.query.startDate;
      this.dateRangePicker.endDate = this.$route.query.endDate;
    }
    /* *********** ************** ************ */
    try {
      /* *********** Get API Data *********** */
      const [trafficSourceAccounts, trackerAccounts, campaign] = await Promise.all([this.$apiStore.trafficSources.accounts(), this.$apiStore.trackers.accounts(), this.$_getCampaign()]);
      const trafficSourceAccount = trafficSourceAccounts.find(({ id }) => id === campaign.traffic_source_account_id);
      // const trackerAccount = trackerAccounts.find(({ id }) => id === campaign.tracker_account_id);
      // const trackerAccount = campaign.tracker_account_id !== 0 ? trackerAccounts.find(({ id }) => id === campaign.tracker_account_id) : { type: { uniqueName: 'ClickFlare' }, name: 'ClickFlare' };
      const trackerAccount = campaign.tracker_account_id === 0 && trackerAccounts.length === 0 ? { type: { uniqueName: 'ClickFlare' }, name: 'ClickFlare' } : trackerAccounts.find(({ id }) => id === campaign.tracker_account_id);
      /* *********** ************ *********** */
      /* *********** Set API Data *********** */
      this.account = trafficSourceAccount || {};
      this.tracker = trackerAccount || {};
      this.campaign = campaign;
      /* *********** ************ *********** */
      /* *********** Set Active Tab ************ */
      this.availableTabs = config.getAvailableTabs(campaign, trafficSourceAccount, trackerAccount);
      this.editableFields = config.editableFields;
      const { query } = this.$route;
      this.activeTab = this.availableTabs.find((tab) => tab.id === query.tab) || this.availableTabs[0];
      if (this.activeTab.id !== query.tab) this.$router.replace({ query: { ...query, tab: this.activeTab.id } });
      /* *********** ************** ************ */
      this.preloader = false;
    } catch (error) {
      console.log(error);
      this.$n_failNotification({ title: error.response.data.message });
    }
  },
  methods: {
    async $_getCampaign() {
      this.preloaderCampaign = true;
      try {
        const campaign = await this.$api.campaigns.getCampaignPerformance(this.$route.params.id, {
          startDate: this.dateRangePicker.startDate,
          endDate: this.dateRangePicker.endDate,
        });
        this.preloaderCampaign = false;
        return campaign;
      } catch (error) {
        this.$n_failNotification({ title: error.response.data.message || 'An error occurred' });
        this.preloaderCampaign = false;
      }
    },
    async $_changeCountryBid(campaign, byCountry, country) {
      try {
        const { id, bid, bid_type } = campaign;
        if (country) {
          const countryBid = byCountry;
          byCountry = campaign.byCountry.filter((item) => item.country !== country);
          byCountry.push({
            country,
            countryBid,
          });
        }
        await this.$api.campaigns.changeCampaignBid(id, bid, bid_type, byCountry);
        this.$n_successNotification({ title: 'Bid successfully updated' });
        this.updateCampaign.byCountry = byCountry;
        return true;
      } catch (error) {
        this.$n_failNotification({ title: error.response.data.message || 'An error occurred' });
      }
    },
    $_tabAction(tab) {
      this.activeTab = tab;
      const { query } = this.$route;
      if (this.activeTab.id !== this.$route.query.tab) this.$router.replace({ query: { ...query, tab: this.activeTab.id } });
    },
    async $_changeDateRangePicker() {
      this.campaign = await this.$_getCampaign();
    },
  },
};
</script>

<style lang="scss">
  .campaign-container {
    &__campaign {
      display: flex;
      border-radius: 0.6rem;
      // box-shadow: $btn-bs-md;
      border: .1rem solid #e5e5e5;
      background: $color-white;
      font-size: 1.3rem;

      & > div {
        border-right: .1rem solid #ebebeb;
        padding: .55rem 1.5rem;
      }

      &__actions {
        display: flex;
        flex-basis: 10%;
        justify-content: center;
      }

      &__name {
        flex-basis: 40%;
        width: 40%;
        display: flex;
        align-items: center;
        justify-content: center;

        .campaign-name {
          overflow: hidden;
          white-space: nowrap;

          span {
            overflow: hidden;
            text-overflow: ellipsis;
          }
        }

        .divider {
          margin: 0 .5rem;
        }

        img {
          height: 2rem;

          &:last-of-type {
            margin-right: .5rem;
          }
        }

        .flags {
          display: flex;
          margin-left: .7rem;

          .flag-item {
            width: 1.3rem;
            height: 1.3rem;
            border: .1rem solid $color-red;
            color: $color-red;
            font-weight: bold;
            margin-right: .5rem;
            font-size: 1rem;
            text-align: center;
            line-height: 1.2rem;
            cursor: pointer;

            &.label-red {
              border-color: $color-red;
              color: $color-red;
            }

            &.label-blue {
              border-color: $color-blue;
              color: $color-blue;
            }

            &.label-blue-steel {
              border-color: $color-green;
              color: $color-green;
            }

            &.label-light-blue {
              border-color: $color-purple;
              color: $color-purple;
            }
          }
        }
      }

      &__status {
        display: flex;
        align-items: center;
        justify-content: center;
        flex-basis: 5%;

        .status-item {
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: default;

          & > span {
            display: flex;
            align-items: center;
          }

          .circle,
          .stats-circle {
            display: flex;
            align-items: center;
            justify-content: center;
            border-radius: 50%;
            height: 1.6rem;
            width:  1.6rem;
            background: rgba(247, 206, 104, 0.3);

            &::after {
              content: '';
              display: block;
              line-height: 2rem;
              border-radius: 50%;
              width: 1rem;
              height: 1rem;
              background: $color-yellow;
            }
          }

          &.status-item-RUNNING,
          &.status-item-running,
          &.status-item-active,
          &.status-item-enabled,
          &.status-item-enabled,
          &.status-item-APPROVED {
            color: #61a633 !important;

            .circle,
            .stats-circle {
              background: rgba(#61a633, 0.3);

              &::after {
                background: #61a633;
              }
            }
          }

          &.status-item-PAUSED,
          &.status-item-paused,
          &.status-item-deleted {
            color: $color-red !important;

            .circle {
              background: rgba(#df3a3a, 0.3);

              &::after {
                background: $color-red;
              }
            }
          }

          &.status-item-OTHER,
          &.status-item-other,
          &.status-item-disabled {
            color: $color-dark-yellow !important;

            .circle {
              background: rgba(#fdbe1d, 0.3);

              &::after {
                background: $color-dark-yellow;
              }
            }
          }

          &.status-item.MODERATION {
            color: $color-blue !important;

            .circle {
              background: rgba(#52a8f8, 0.3);

              &::after {
                background: $color-blue;
              }
            }
          }
        }
      }

      &__budget {
        position: relative;
        flex: 1 1 0;
        display: flex;
        align-items: center;
        justify-content: center;
        color: $color-light-blue;
        padding: 0 1rem !important;
        // overflow: hidden;
        padding-top: .8rem !important;
        font-size: 1.2rem;

        .label-tag {
          // display: none;
          position: absolute;
          left: 0;
          top: .2rem;
          width: 100%;
          text-align: center;
          // background: #7676ff;
          color: $text-primary;
          height: 1.3rem;
          font-size: 1.1rem;
          line-height: 1.2;
        }

        .live-edit {
          input {
            height: 2rem;
            border-right: .1rem solid $color-light-blue;
          }
        }
      }

      :last-child {
        border-right: 0;
      }
    }

    &__tabs {
      margin-bottom: 2rem;

      h5 {
        font-size: 1.8rem;

        .reporting-timezone-title {
          font-size: 1.3rem;
          color: $text-secondary;
          margin-left: .3rem;
        }
      }

      &__content {
        .tab-item {
          display: inline-block;
          padding-right: .5rem;
          margin-right: 1.8rem;
          font-size: 1.4rem;
          font-weight: 500;
          color: #252D41;
          cursor: pointer;

          &:last-of-type {
            padding-right: 0;
            margin-right: 0;
          }

          &.tab-item-active {
            color: $color-light-blue;
            border-bottom: .2rem solid $color-light-blue;
          }
        }
      }
    }

    .optimizer-date-range {
      & > .reportrange-text {
        // box-shadow: $btn-bs-md;
        // border: none !important;
      }
    }
  }

  // .tab-item {
  //   cursor: pointer;
  //   float: left;
  //   padding-right: 5px;
  //   margin-right: 7px;
  //   color: gray;
  //   &:last-child {
  //     padding-right: 0px;
  //     margin-right: 0px;
  //   }
  // }
  // .tab-item-active {
  //   color: black;
  // }
</style>
